<!-- The recipe: what she made, in the order she made it, with what she said
     while she was making it sitting in the margin beside the step.

     ONE SCREEN, THREE MODES, and that is the whole navigation model.

     Before this there were two screens — a recipe sheet and a capture screen —
     that looked almost identical and behaved differently, which is most of why
     moving around the app felt confusing: you could not tell by looking which
     one you were on, and the same gesture did different things on each. They
     are now one screen in one of three modes, and each mode has exactly one
     obvious thing to do:

       READING   the default. The recipe, quiet, nothing to change by accident.
                 Primary action: Cook this. Edit and the recipe menu sit in the
                 header. There is no Record button — you are reading, and this
                 is the mode a recipe sits in for years.
       EDITING   writing it down and fixing it. Primary action: Record. Steps
                 and ingredients become editable, each step grows its menu.
                 The toolbar goes dark so you can never be in this mode without
                 noticing — a mode you are in unawares is its own bug.
       COOKING   one step at a time, big type, phone on the bench, hands busy.
                 Entered from reading, because that is why you opened the
                 recipe. Record earns its place here too: this is the moment
                 the app exists for, and it files against the step you are
                 standing on.

     Reading is always the way back — Done from editing, All steps from cooking
     — so there is one place to return to and you cannot get lost in a mode you
     did not mean to enter. -->
<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonContent,
   IonToast, onIonViewWillLeave,
} from '@ionic/vue';
import SheetStep from '../components/SheetStep.vue';
import HdDialog from '../components/HdDialog.vue';
import HdSheet from '../components/HdSheet.vue';
import MarginNote from '../components/MarginNote.vue';
import NoteRow from '../components/NoteRow.vue';
import HdIcon from '../components/HdIcon.vue';
import { formatQuantity, parseQuantity } from '../lib/quantity.js';
import { getAudioUrl, deleteAudio, isDurableMedia } from '../lib/audio-store.js';
import { isAudible } from '../lib/notes.js';
import { skin } from '../lib/skin.js';
import { useTake } from '../lib/use-take.js';
import { duplicateRecipe, deleteRecipeDeep } from '../lib/recipe-actions.js';
import {
  recipes, cooks, ingredients, steps, marginNotes,
  recipesReady, cooksReady, ingredientsReady, stepsReady, marginNotesReady,
  addMarginNote, removeMarginNote, saveMarginNote,
  saveRecipe, saveIngredient, removeIngredient, addIngredient,
  addStep,
} from '../store.js';

const route = useRoute();
const router = useRouter();

/* The generated pages read route.params.id, which this route never sets.
   The param is recipeId. */
const recipeId = computed(() => route.params.recipeId);

/* Nothing is read from the store until it has hydrated from IndexedDB.
   Reading first means every reload flashes "Not found" before the rows land,
   which tells the reader their recipe is gone. */
const loaded = ref(false);
onMounted(async () => {
  await Promise.all([
    recipesReady, cooksReady, ingredientsReady, stepsReady, marginNotesReady,
  ]);
  loaded.value = true;
  /* A recipe with nothing written in it has nothing to read, so it opens in
     the mode that can fix that. Starting a NEW recipe is therefore just this
     screen with a blank page, rather than a different screen called Capture. */
  if (recipe.value && recipeSteps.value.length === 0) mode.value = 'edit';
});

const same = (a, b) => String(a) === String(b);
const byPosition = (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0);
const byTime = (a, b) => String(a.recordedAt ?? '').localeCompare(String(b.recordedAt ?? ''));

const recipe = computed(() => recipes.value.find((r) => same(r.id, recipeId.value)) ?? null);
const cook = computed(() => {
  const r = recipe.value;
  return r ? cooks.value.find((c) => same(c.id, r.cookId)) ?? null : null;
});
const cookName = computed(() => cook.value?.name || 'the cook');

const attribution = computed(() =>
  [cook.value?.name, cook.value?.kitchen].filter(Boolean).join(' · ')
);

/* "Nonna's book" / "Nonna's tin" — the back button says where you are going
   back TO, in the words the person's own setting has made true. */
const backLabel = computed(() =>
  `${cookName.value}’s ${skin.value === 'tin' ? 'tin' : 'book'}`
);

const recipeIngredients = computed(() =>
  ingredients.value.filter((i) => same(i.recipeId, recipeId.value)).slice().sort(byPosition)
);
const recipeSteps = computed(() =>
  steps.value.filter((s) => same(s.recipeId, recipeId.value)).slice().sort(byPosition)
);
const allNotes = computed(() =>
  marginNotes.value.filter((n) => same(n.recipeId, recipeId.value)).slice().sort(byTime)
);

const stepIds = computed(() => new Set(recipeSteps.value.map((s) => String(s.id))));

/* A note with no stepId was recorded before the first step, so it belongs to
   the recipe and sits at the top of the margin. A note pointing at a step that
   has since been deleted lands here too — surfacing an orphan beats losing it. */
const recipeNotes = computed(() =>
  allNotes.value.filter((n) => !n.stepId || !stepIds.value.has(String(n.stepId)))
);
const notesFor = (stepId) => allNotes.value.filter((n) => same(n.stepId, stepId));

const quietMargin = computed(() => allNotes.value.length === 0);

/* One definition, in lib/notes.js, because this predicate was written four
   times and three of the copies counted a photograph as a voice. */
const audible = isAudible;

// ------------------------------------------------------------------ mode

/** 'read' | 'edit' | 'cook'. Reading is the default and the way back. */
const mode = ref('read');

/* A recipe is "kept" once someone has written something into it and closed
   the editor. `capturing` used to be flipped by a Keep it button on a screen
   that no longer exists; nothing else ever set it, so every recipe made on
   the sheet stayed "Still being recorded" forever and counted for nothing on
   the shelf, in the booklet, or in Settings. */
function keep() {
  const r = recipe.value;
  if (!r || r.status === 'kept') return;
  if (recipeSteps.value.length || recipeIngredients.value.length || allNotes.value.length) {
    saveRecipe({ ...r, status: 'kept' });
  }
}
function toRead() { keep(); mode.value = 'read'; }
function toEdit() { mode.value = 'edit'; }
function toCook() {
  cookStep.value = 0;
  ingredientsOpen.value = false;
  mode.value = 'cook';
}

/* Leaving cooking or editing must not leave her talking, and must not leave a
   half-typed step focused under a toolbar that is no longer there. */
watch(mode, () => { stopAudio(); });

// ---------------------------------------------------------------- playback

const playingNoteId = ref(null);
const progress = ref(0);
let audio = null;

/* A note's mediaUrl is a reference, not a URL: the bytes have to be fetched
   from lib/audio-store.js and turned into an object URL before anything can
   play. That round trip is why this counter exists — someone can press a
   second note while the first is still resolving, and only the most recent
   press may start. Every stop bumps it, so an in-flight press that has been
   overtaken (or a page that has since been left) quietly gives up. */
let playToken = 0;

const isPlaying = (id) => playingNoteId.value != null && same(playingNoteId.value, id);

function stopAudio() {
  playToken += 1;
  if (audio) {
    audio.pause();
    audio.ontimeupdate = null;
    audio.onended = null;
    audio.onerror = null;
    audio = null;
  }
  playingNoteId.value = null;
  progress.value = 0;
}

/* A note with no audio is not a broken button. It opens what was written —
   which is the whole of a written note, and all there is of a voice note whose
   recording has not landed yet.

   `reason` distinguishes three different truths, because telling someone
   "nothing was recorded" about a recording they remember making is a lie:
     silent — there was never any audio on this note
     gone   — there was, and it is not on this device any more
     failed — the audio is here and the browser would not play it */
const reader = ref(null);
function openReader(note, reason) {
  /* The `gone` sentence is the hardest one in the app, and it is read at the
     worst moment there is: the mark is in the margin, the note says she spoke,
     and the voice will not come. It has to be final — anyone told "try again
     later" here will go looking through settings, reinstall, and find nothing
     — and it must not send them back to a kitchen that may not be there. So:
     what happened, why nothing can be recovered, and what is still on the
     page. No apology, no next time, and no comfort the app cannot back up. */
  const fallback =
    reason === 'failed'
      ? 'That recording could not be played. The file may not have finished saving.'
      : reason === 'gone'
        ? 'That recording is no longer on this device. It was never stored anywhere else, so there is nothing to restore it from. The rest of the recipe — the steps, the amounts, the other notes — is still here.'
        : 'Nothing was recorded for this one yet.';
  /* THE GONE SENTENCE HAS TO SURVIVE THE NOTE HAVING WORDS.
   *
   * This was `note.body || fallback`, so a voice note carrying ANY text never
   * showed it — and the notes most likely to have been evicted are the oldest,
   * which are the ones most likely to have had something typed beside them. A
   * person tapped to hear their mother, got her transcript and a Close button,
   * and was never told the recording was gone. They tap it again. And again.
   *
   * Both now, in the right order: what happened first, then what she wrote. */
  const body = note.body && note.body.trim();
  const message = reason === 'silent' && body
    ? body
    : [fallback, body].filter(Boolean).join('\n\n');

  /* Not `${note.kind} note from …` — that is a database enum rendered to a
     grieving person as "voice note from Nonna", lower-case and all. */
  const heading = note.kind === 'written'
    ? 'What they wrote'
    : note.kind === 'photo'
      ? `A photograph from ${cookName.value}`
      : `What ${cookName.value} said`;

  reader.value = { header: heading, message };
}

async function play(id) {
  const note = allNotes.value.find((n) => same(n.id, id));

  /* Pressing the note that is playing stops it. Pressing another one stops
     this one first — two voices at once is never what anyone meant. */
  if (isPlaying(id)) { stopAudio(); return; }
  stopAudio();
  if (!note) return;

  const token = playToken;

  if (!note.mediaUrl) { openReader(note, 'silent'); return; }

  const src = await getAudioUrl(note.mediaUrl);
  if (token !== playToken) return;

  /* Nothing behind the reference: a note written before recordings were kept
     durably, or audio the browser has since evicted. Either way the note is
     still a note — it opens what was written instead of doing nothing. */
  if (!src) { openReader(note, 'gone'); return; }

  const el = new Audio(src);
  el.ontimeupdate = () => {
    progress.value = el.duration > 0 ? Math.min(1, el.currentTime / el.duration) : 0;
  };
  el.onended = () => stopAudio();
  el.onerror = () => { stopAudio(); openReader(note, 'failed'); };
  audio = el;
  playingNoteId.value = note.id;
  progress.value = 0;
  el.play().catch(() => { stopAudio(); openReader(note, 'failed'); });
}

/* Leaving the page must not leave her talking in the background. */
onBeforeUnmount(stopAudio);

// -------------------------------------------------------------- step menu

/* One control per step, and everything it can do is named in one list. It
   exists only in editing: on the page you read from while cooking, the words
   are words, and a stray thumb must not open an editor over them. */
const stepRefs = new Map();
function setStepRef(id, el) {
  if (el) stepRefs.set(String(id), el);
  else stepRefs.delete(String(id));
}

/* THE STEP MENU IS GONE, and the page is one tap shorter for it.
 *
 * It held four items behind a mark of three dots, and the first useful one was
 * "Fix the words" — which tapping the words already did. Now the step wears a
 * pencil that edits, and the other three (record a note on it, write one on
 * it, delete it) sit inside the open editor, where you are already looking at
 * the step they act on. One mark per step, doing the thing it depicts. */

// ------------------------------------------------------------ recipe menu

/* THE RECIPE'S OWN ACTIONS live on the recipe, behind one control beside Edit
   — not in Settings, and not buried inside editing. You decide to copy or bin
   a recipe while you are looking at it.

   Delete is the only irreversible thing in this app, so it is the only thing
   that asks twice, and the question names what is actually lost: her voice,
   not "3 records". */
const recipeMenuOpen = ref(false);
const confirmDelete = ref(false);
const toast = ref('');

/* WHAT IS ACTUALLY LOST, COUNTED HONESTLY.
 *
 * This was `kind !== 'written' && mediaUrl`, which counts a PHOTOGRAPH as a
 * voice — so the delete dialog told someone "the one note in Dad's voice goes
 * with it" about a picture. The one dialog in this app that must be exactly
 * true was making a false claim about a dead person's voice, and the person
 * reading it has no way to check.
 *
 * `audible` is the same gate the rows and the cook cards use: is there
 * something to HEAR. Photographs are counted separately and named separately,
 * because losing a picture is a real loss too and lumping it in with the
 * recordings would be the same error pointing the other way. */
const voiceNoteCount = computed(() => allNotes.value.filter(audible).length);
const photoNoteCount = computed(
  () => allNotes.value.filter((n) => n.kind === 'photo' && n.mediaUrl).length
);

const recipeMenuButtons = computed(() => [
  { text: 'Make a copy', handler: () => { recipeMenuOpen.value = false; makeCopy(); } },
  { text: 'Send to family', handler: () => { recipeMenuOpen.value = false; sendCopy(); } },
  { text: 'Print the page', handler: () => { recipeMenuOpen.value = false; printPage(); } },
  {
    text: 'Delete this recipe',
    role: 'destructive',
    handler: () => { recipeMenuOpen.value = false; confirmDelete.value = true; },
  },
  { text: 'Cancel', role: 'cancel' },
]);

/* Said in human terms rather than the database's, and a recipe with no
   recordings loses nothing that cannot be typed again — saying "3 recordings"
   about nought of them would be the app crying wolf on the one dialog in it
   that matters.

   THE COOK'S NAME, NOT A PRONOUN. The app is not told whether a cook is a
   he, a she or a they, and the person reading this dialog is looking at
   someone they love; guessing wrong here is worse than the extra word. The
   name is also simply more specific than "their". */
const deleteMessage = computed(() => {
  const voices = voiceNoteCount.value;
  const photos = photoNoteCount.value;
  const whose = cook.value?.name ? `${cookName.value}’s` : 'their';

  /* Each kind named as itself. A recipe with no recordings loses nothing that
     cannot be typed again, and saying "3 recordings" about nought of them
     would be the app crying wolf on the one dialog that matters. */
  const parts = [];
  if (voices === 1) parts.push(`the one note in ${whose} voice`);
  else if (voices > 1) parts.push(`the ${voices} notes in ${whose} voice`);
  if (photos === 1) parts.push('the photograph');
  else if (photos > 1) parts.push(`the ${photos} photographs`);

  const heart = parts.length === 0
    ? 'The steps and the amounts go with it.'
    : `The steps and the amounts go with it, and so ${parts.length > 1 ? 'do' : 'does'} ${parts.join(' and ')}.`;

  /* The recordings sentence only where there ARE recordings. On a recipe that
     has a photograph and no voice, "nothing else in the app removes a
     recording" is a non-sequitur at the one moment the reader is weighing
     every word. */
  const irreversible = voices > 0
    ? ' Nothing else in the app removes a recording, and this cannot be undone.'
    : ' This cannot be undone.';
  return heart + irreversible;
});

const deleteButtons = computed(() => [
  { text: 'Keep it', role: 'cancel' },
  { text: 'Delete', role: 'destructive', handler: () => { doDelete(); } },
]);

async function makeCopy() {
  const row = recipe.value;
  if (!row) return;
  const id = await duplicateRecipe(row.id);
  if (!id) { toast.value = 'That could not be copied.'; return; }
  /* Straight into the copy, in editing: the reason anyone copies a recipe is
     to change something in it, and leaving them on the original with a message
     about a copy elsewhere is a note, not an outcome. */
  toast.value = `Copied. You are looking at the copy — the original is untouched.`;
  await router.replace({ name: 'recipe-sheet', params: { recipeId: id } });
  mode.value = 'edit';
}

async function doDelete() {
  const row = recipe.value;
  if (!row) return;
  stopAudio();
  await deleteRecipeDeep(row.id);
  /* Back to the book it was in, not back a screen: the screen behind might be
     this recipe again, from a deep link. */
  router.replace(backHref.value);
}

/** The recipe as plain text — the form anything outside this app can read. */
function asText() {
  const row = recipe.value;
  if (!row) return '';
  const lines = [row.title || 'Untitled'];
  if (attribution.value) lines.push(attribution.value);
  if (row.occasion) lines.push(row.occasion);
  if (recipeIngredients.value.length) {
    lines.push('', 'Ingredients');
    for (const ing of recipeIngredients.value) {
      lines.push(`  ${[formatQuantity(ing), ing.item].filter(Boolean).join(' ')}`);
    }
  }
  if (recipeSteps.value.length) {
    lines.push('', 'Method');
    recipeSteps.value.forEach((s, i) => {
      lines.push(`  ${i + 1}. ${s.body}`);
      /* A written note travels with its step. A recording cannot: it is a blob
         in this device's IndexedDB and there is nowhere to put it in a text
         message. Saying so beats a copy that quietly arrives without her. */
      for (const n of notesFor(s.id)) {
        if (n.body) lines.push(`     — ${n.body}`);
        else if (n.mediaUrl) lines.push(`     — [${cookName.value} said something here]`);
      }
    });
  }
  return lines.join('\n');
}

async function sendCopy() {
  const text = asText();
  if (!text) return;
  const title = recipe.value?.title || 'A recipe';
  /* The share sheet where there is one — that is the control people already
     know for "send this to someone". Clipboard where there is not. */
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title, text });
      return;
    }
    await navigator.clipboard.writeText(text);
    toast.value = voiceNoteCount.value
      ? 'Copied as text. The recordings stay on this device — they cannot be sent yet.'
      : 'Copied as text, ready to paste.';
  } catch {
    /* A cancelled share sheet lands here too, and is not a failure worth a
       message. Only say something if there is nothing they can do next. */
  }
}

function printPage() {
  if (typeof window === 'undefined' || typeof window.print !== 'function') {
    toast.value = 'Printing is not available on this device.';
    return;
  }
  /* Reading, not editing: nobody wants dashed underlines and a dark toolbar on
     a printed page, and the print stylesheet is written against this mode. */
  mode.value = 'read';
  nextTick(() => window.print());
}

// ------------------------------------------------------------ edit recipe

/* Editing lives on the sheet because the sheet is where the mistake is seen. */
const detailsOpen = ref(false);
const detailsInputs = computed(() => [
  { name: 'title', type: 'text', value: recipe.value?.title ?? '', placeholder: 'What is it called?' },
  { name: 'occasion', type: 'text', value: recipe.value?.occasion ?? '', placeholder: 'Bottling day, every February' },
  { name: 'serves', type: 'number', value: recipe.value?.serves ?? '', placeholder: 'Serves how many?', min: 0 },
]);
function openDetails() {
  if (mode.value !== 'edit') return;
  detailsOpen.value = true;
}
const detailsButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Save',
    handler: (data) => {
      const row = recipe.value;
      if (!row) return;
      const title = String(data?.title ?? '').trim();
      const serves = Number(data?.serves);
      saveRecipe({
        ...row,
        /* An empty title is not a rename — the sheet falls back to "Untitled"
           for display, but blanking the field by accident should not wipe the
           name the recipe already had. */
        title: title || row.title,
        occasion: String(data?.occasion ?? '').trim(),
        serves: Number.isFinite(serves) && serves > 0 ? serves : 0,
      });
    },
  },
];

/* One ingredient, one box — the same single field capture uses, so "10 kg" is
   parsed and "a finger of space" is kept in those words, and editing cannot
   quietly impose a schema the original entry was never held to. */
const ingredientId = ref(null);
const editingIngredient = computed(
  () => (ingredientId.value == null ? null : recipeIngredients.value.find((i) => same(i.id, ingredientId.value)) ?? null)
);
function openIngredient(ing) {
  if (mode.value !== 'edit') return;
  ingredientId.value = ing.id;
}
const ingredientInputs = computed(() => {
  const ing = editingIngredient.value;
  return [
    { name: 'amount', type: 'text', value: ing ? formatQuantity(ing) : '', placeholder: '10 kg' },
    { name: 'item', type: 'text', value: ing?.item ?? '', placeholder: 'Roma tomatoes' },
  ];
});
const ingredientButtons = computed(() => [
  {
    text: 'Remove',
    role: 'destructive',
    handler: () => {
      const ing = editingIngredient.value;
      if (ing) removeIngredient(ing.id);
    },
  },
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Save',
    handler: (data) => {
      const ing = editingIngredient.value;
      if (!ing) return;
      const item = String(data?.item ?? '').trim();
      saveIngredient({ ...ing, ...parseQuantity(String(data?.amount ?? '')), item: item || ing.item });
    },
  },
]);

/* Adding, from the page itself. Until now an ingredient or a step could only
   be added on the capture screen, so a recipe you had finished recording could
   be corrected but never extended — which is not how anyone's cooking works. */
const newIngredientOpen = ref(false);
const newIngredientInputs = [
  { name: 'amount', type: 'text', placeholder: '10 kg' },
  { name: 'item', type: 'text', placeholder: 'Roma tomatoes' },
];
const newIngredientButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Add',
    handler: (data) => {
      const item = String(data?.item ?? '').trim();
      if (!item || !recipe.value) return;
      const last = recipeIngredients.value[recipeIngredients.value.length - 1];
      addIngredient({
        recipeId: recipe.value.id,
        ...parseQuantity(String(data?.amount ?? '')),
        item,
        position: (Number(last?.position) || recipeIngredients.value.length) + 1,
      });
    },
  },
];

const newStepOpen = ref(false);
const newStepInputs = [
  { name: 'body', type: 'textarea', placeholder: 'What happens next?', attributes: { rows: 3 } },
];
const newStepButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Add',
    handler: (data) => {
      const body = String(data?.body ?? '').trim();
      if (!body || !recipe.value) return;
      const last = recipeSteps.value[recipeSteps.value.length - 1];
      addStep({
        recipeId: recipe.value.id,
        body,
        position: (Number(last?.position) || recipeSteps.value.length) + 1,
      });
    },
  },
];

// ----------------------------------------------------------------- manage

/* Press and hold a mark to play it or delete it. A gesture rather than a
   delete control parked beside every note: the margin has to keep looking like
   paper, and a row of little bins in it would end that. The cost is that a
   gesture teaches nobody, so the line under the method says it in words. */
const manageId = ref(null);
const manageNote = computed(
  () => (manageId.value == null ? null : allNotes.value.find((n) => same(n.id, manageId.value)) ?? null)
);
function openManage(id) { manageId.value = id; }

const manageButtons = computed(() => {
  const note = manageNote.value;
  if (!note) return [];
  return [
    {
      /* The same "is there something to hear" gate the rows use. `kind` alone
         offered to Play a voice note whose recording is gone. */
      text: audible(note) ? 'Play it' : 'Read it',
      handler: () => { const id = note.id; manageId.value = null; play(id); },
    },
    /* A note filed against the wrong step is fixed here rather than being
       aimed correctly in advance. */
    ...recipeSteps.value
      .filter((s) => !same(s.id, note.stepId))
      .map((s, i) => ({
        text: `Move to step ${i + 1}`,
        handler: () => { moveNote(note, s.id); manageId.value = null; },
      })),
    ...(note.stepId
      ? [{
          text: 'Move to the recipe',
          handler: () => { moveNote(note, ''); manageId.value = null; },
        }]
      : []),
    {
      text: 'Delete',
      role: 'destructive',
      handler: () => { removeNote(note); manageId.value = null; },
    },
    { text: 'Cancel', role: 'cancel' },
  ];
});

/** Refile a note. The recording is untouched; only its step changes. */
function moveNote(note, stepId) {
  saveMarginNote({ ...note, stepId });
}

/* Deleting a note deletes the recording with it. Otherwise every deleted note
   leaves its audio in IndexedDB forever, invisible, counting against the quota
   that decides whether the NEXT recording can be saved. */
async function removeNote(note) {
  if (isPlaying(note.id)) stopAudio();
  const ref = note.mediaUrl;
  removeMarginNote(note.id);
  if (isDurableMedia(ref)) {
    try { await deleteAudio(ref); } catch { /* the row is gone either way */ }
  }
}

// -------------------------------------------------------------------- pin

const pinStepId = ref(null);
const pinOpen = ref(false);
const pinInputs = [
  { name: 'body', type: 'textarea', placeholder: 'What did they say?', attributes: { rows: 3 } },
];
const pinButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Pin it',
    handler: (data) => {
      const body = String(data?.body ?? '').trim();
      if (!body || !recipe.value) return;
      addMarginNote({
        recipeId: recipe.value.id,
        stepId: pinStepId.value,
        kind: 'written',
        body,
        mediaUrl: '',
        durationSeconds: 0,
      });
    },
  },
];
function openPin(stepId) {
  pinStepId.value = stepId;
  pinOpen.value = true;
}

// ----------------------------------------------------------------- cooking

/* One step at a time. The index is local to this visit: where you were up to
   last Sunday is not a fact about the recipe, and restoring it would put
   somebody at step 6 of a dish they have not started. */
const cookStep = ref(0);
/* Closed each time you enter cooking mode: the step is what you came for, and
   a panel that remembers being open buries it on the next recipe. */
const ingredientsOpen = ref(false);
const cookIndex = computed(() => Math.min(cookStep.value, Math.max(0, recipeSteps.value.length - 1)));
const currentStep = computed(() => recipeSteps.value[cookIndex.value] ?? null);
const currentNotes = computed(() => (currentStep.value ? notesFor(currentStep.value.id) : []));
const onLastStep = computed(() => cookIndex.value >= recipeSteps.value.length - 1);

function prevStep() {
  stopAudio();
  cookStep.value = Math.max(0, cookIndex.value - 1);
}
function nextStep() {
  stopAudio();
  if (onLastStep.value) { toRead(); return; }
  cookStep.value = cookIndex.value + 1;
}

// ---------------------------------------------------------------- recording

/* THE RECORDER IS ON THE SHEET NOW.
 *
 * Pressing Record used to push you onto CapturePage — a screen in a different
 * visual language, with its own ingredient list, its own add-step field and
 * its own step numbering, reached at the exact second someone started talking.
 * The audio path is unchanged and shared (lib/use-take.js); only its mount
 * point moved, onto the page you were already looking at.
 *
 * The step is resolved at SAVE time, not at start time: you can write another
 * step while the recording runs, and the note belongs where the bar says it is
 * going now. */
/* '$recipe' = explicitly aimed at the recipe level (pre-step note).
   '' = no explicit aim; falls back to the most recent step. */
const RECIPE_LEVEL = '$recipe';
const aimedStepId = ref('');
const aimedStep = computed(() =>
  aimedStepId.value === RECIPE_LEVEL
    ? null
    : (recipeSteps.value.find((s) => same(s.id, aimedStepId.value)) ?? null)
);
/* No control aims a recording by default. It lands on the step you just wrote,
   which is what happens in a kitchen — she says a step, you write it down, she
   explains it, you record. Aiming it at an older step is done from that step's
   own Record action, which sets this. Aiming at the recipe itself (pre-step)
   is done from the recipe-level Record button, which sets RECIPE_LEVEL. */
const takeStep = computed(() =>
  aimedStepId.value === RECIPE_LEVEL
    ? null   // explicitly recipe-level
    : (aimedStep.value ?? recipeSteps.value[recipeSteps.value.length - 1] ?? null)
);
const takeStepNumber = computed(() => {
  const step = takeStep.value;
  if (!step) return 0;
  return recipeSteps.value.findIndex((s) => same(s.id, step.id)) + 1;
});

const take = useTake({
  recipeId,
  stepId: () => takeStep.value?.id ?? '',
  whereLabel: () => (takeStep.value ? `step ${takeStepNumber.value}` : 'the recipe'),
});

/** Where the next recording will land, said in words. */
const takeDestination = computed(() =>
  takeStep.value
    ? `Goes on step ${takeStepNumber.value} — the one you just wrote`
    : 'Goes on the recipe, before the first step'
);

/* Start recording against a NAMED step: from a step's own editor, or from the
   step you are standing on in cooking mode. */
async function recordOnStep(stepId) {
  aimedStepId.value = stepId ? String(stepId) : '';
  if (mode.value === 'read') mode.value = 'edit';
  if (!take.recording.value) await take.toggle();
}

/* Start recording against the recipe itself (a note that lives before step 1). */
async function recordOnRecipe() {
  aimedStepId.value = RECIPE_LEVEL;
  if (mode.value === 'read') mode.value = 'edit';
  await take.toggle();
}

/* Leaving the page must not take the take with it, and must not leave a
   written recipe filed as a draft. */
onIonViewWillLeave(() => { take.flush(); keep(); });

// -------------------------------------------------------------- navigation

const backHref = computed(() => (cook.value ? `/cook/${cook.value.id}` : '/'));
const goBack = () => router.back();

/* NOTHING PUSHES TO THE CAPTURE ROUTE ANY MORE. It is still routable, so an
   old deep link still opens something that works, but no control in the app
   sends you there: recording happens on the sheet, in editing mode, on the
   page you were already reading. See `recordOnStep` above. */
</script>

<template>
  <IonPage>
    <IonHeader class="hd-bar" :class="{ editing: mode === 'edit' }">
      <IonToolbar>
        <!-- READING: where you came from, and the two things you can do to the
             recipe rather than with it. -->
        <template v-if="mode === 'read'">
          <IonButtons slot="start">
            <IonBackButton :default-href="backHref" :text="backLabel" />
          </IonButtons>
          <IonButtons slot="end">
            <button class="bar-btn" type="button" @click="toEdit">
              <HdIcon name="pencil" :size="15" />
              <span>Edit</span>
            </button>
            <button class="bar-btn icon-only" type="button" aria-label="What to do with this recipe" @click="recipeMenuOpen = true">
              <HdIcon name="dots" :size="18" />
            </button>
          </IonButtons>
        </template>

        <!-- EDITING: unmistakably a different mode. The dark bar is not
             decoration — a mode you are in without noticing is a bug. -->
        <template v-else-if="mode === 'edit'">
          <IonButtons slot="start">
            <span class="mode-flag">EDITING</span>
          </IonButtons>
          <IonButtons slot="end">
            <button class="bar-btn strong" type="button" @click="toRead">Done</button>
          </IonButtons>
        </template>

        <!-- COOKING: one way out, and it is the mode you came from. -->
        <template v-else>
          <IonButtons slot="start">
            <button class="bar-btn" type="button" @click="toRead">
              <HdIcon name="chevron-left" :size="16" :weight="1.9" />
              <span>All steps</span>
            </button>
          </IonButtons>
          <IonButtons slot="end">
            <span class="bar-title">{{ recipe?.title }}</span>
          </IonButtons>
        </template>
      </IonToolbar>
    </IonHeader>

    <IonContent :fullscreen="true" class="hd-content">
      <!-- Paper, not white, while the store hydrates. -->
      <div v-if="!loaded" class="hd-page waiting" aria-hidden="true"></div>

      <!-- A record that does not exist is a real state, not a crash: a deep
           link can outlive the thing it points at. -->
      <div v-else-if="!recipe" class="hd-page missing">
        <h2 class="missing-h">Not found</h2>
        <p class="missing-p">That is no longer here. It may have been deleted since this link was made.</p>
        <button class="hd-figure back" type="button" @click="goBack">Go back</button>
      </div>

      <!-- ─────────────────────────── COOKING ─────────────────────────── -->
      <div v-else-if="mode === 'cook'" class="hd-page cooking">
        <!-- How far in you are, as a row of ruled lines rather than a number:
             this is read at a glance with a wooden spoon in the other hand. -->
        <!-- Hidden for a one-step recipe: a single full-width red bar across
             the top of the screen reads as an error banner, not as progress. -->
        <div v-if="recipeSteps.length > 1" class="progress" role="img" :aria-label="`Step ${cookIndex + 1} of ${recipeSteps.length}`">
          <span
            v-for="(s, i) in recipeSteps"
            :key="s.id"
            class="pip"
            :class="{ done: i < cookIndex, now: i === cookIndex }"
          ></span>
        </div>

        <p class="cook-count">STEP {{ cookIndex + 1 }} OF {{ recipeSteps.length }}</p>

        <!-- THE PAPER COMES WITH YOU.
             Cooking mode used to drop the ruling, the margin and the red rule
             — the three things that make this app itself — at the exact moment
             the phone is flat on the bench and being looked at hardest. It is
             the same page, ruled at double pitch because the writing is double
             size: 56px is two rules, so the two views share one grid. -->
        <div class="cook-field">
          <div class="cook-rule" aria-hidden="true"></div>
          <p class="cook-body">{{ currentStep?.body }}</p>
        </div>

        <!-- What she said about THIS step, at the size you can hit wet-handed. -->
        <div v-for="note in currentNotes" :key="note.id" class="hd-card said">
          <button
            class="said-play"
            type="button"
            :data-playing="isPlaying(note.id) ? 'true' : 'false'"
            :class="{ written: !audible(note) }"
            :aria-label="isPlaying(note.id) ? 'Stop' : (audible(note) ? `Play what ${cookName} said` : 'Read this note')"
            @click="play(note.id)"
          >
            <HdIcon v-if="!audible(note)" name="lines" :size="20" />
            <HdIcon v-else :name="isPlaying(note.id) ? 'pause' : 'play'" :size="19" />
          </button>
          <div class="said-text">
            <p class="said-who">
              {{ audible(note) ? cookName : 'Written down' }}
              <span v-if="audible(note) && note.durationSeconds" class="hd-figure">{{ Math.round(note.durationSeconds) }}s</span>
            </p>
            <p v-if="note.body" class="said-body">{{ note.body }}</p>
          </div>
        </div>

        <div class="cook-spacer"></div>

        <!-- THE AMOUNTS, WITHOUT LEAVING THE STEP.
             They were two taps away — back to All steps, find the line, come
             again — which is the one navigation nobody does with their hands
             in the mince. The app cannot know which ingredients belong to
             which step (nothing in the data model says so, and guessing would
             be the same kind of lie as counting a photograph as a voice), so
             it offers all of them, closed, one tap down. -->
        <div v-if="recipeIngredients.length" class="cook-ing">
          <button
            class="cook-ing-toggle"
            type="button"
            :aria-expanded="ingredientsOpen ? 'true' : 'false'"
            @click="ingredientsOpen = !ingredientsOpen"
          >
            <HdIcon :name="ingredientsOpen ? 'chevron-down' : 'chevron-right'" :size="15" />
            <span>{{ ingredientsOpen ? 'Hide the amounts' : 'The amounts' }}</span>
          </button>

          <ul v-if="ingredientsOpen" class="cook-ing-list">
            <li v-for="ing in recipeIngredients" :key="ing.id" class="cook-ing-row">
              <span class="hd-figure cook-qty">{{ formatQuantity(ing) || '—' }}</span>
              <span class="cook-item">{{ ing.item }}</span>
            </li>
          </ul>
        </div>

        <!-- Record earns a place here because this is the moment it exists
             for, and it files against the step you are standing on. -->
        <button class="cook-record" type="button" @click="recordOnStep(currentStep?.id)">
          <HdIcon name="record" :size="14" />
          <span>Record a note on this step</span>
        </button>
      </div>

      <!-- ──────────────────── READING and EDITING ────────────────────── -->
      <article v-else class="hd-page sheet">
        <div class="hd-sheet-card">
          <header class="head hd-sheet-head">
            <button
              class="title title-btn"
              type="button"
              :disabled="mode !== 'edit'"
              :aria-label="mode === 'edit' ? 'Rename this recipe' : undefined"
              @click="openDetails"
            >
              {{ recipe.title || 'Untitled' }}
            </button>
            <p v-if="attribution" class="attribution">{{ attribution }}</p>
            <button
              v-if="mode === 'edit' || recipe.occasion"
              class="attribution occasion-btn"
              type="button"
              :disabled="mode !== 'edit'"
              @click="openDetails"
            >
              {{ recipe.occasion || 'When is it made?' }}
            </button>
          </header>

          <!-- ONE SHEET. The ingredients used to sit in a raised block floating
               above ruled paper — an app card pasted onto a page. They are both
               things she wrote, on the same page, so they share one ruling, one
               red margin and one 28px rhythm. -->
          <div class="hd-field">
            <div class="hd-field-rule" aria-hidden="true"></div>

            <template v-if="recipeIngredients.length || mode === 'edit'">
              <div class="hd-row">
                <div class="hd-row-margin" aria-hidden="true"></div>
                <div class="hd-row-body"><h2 class="hd-label">Ingredients</h2></div>
              </div>

              <div v-for="ing in recipeIngredients" :key="ing.id" class="hd-row">
                <div class="hd-row-margin" aria-hidden="true"></div>
                <div class="hd-row-body">
                  <!-- One target for the whole line: the amount and the thing
                       are one fact, and separating them would mean aiming at a
                       13px figure with a wet thumb. -->
                  <button
                    class="ing-btn"
                    type="button"
                    :disabled="mode !== 'edit'"
                    :aria-label="mode === 'edit' ? `Edit ${ing.item}` : undefined"
                    @click="openIngredient(ing)"
                  >
                    <span class="hd-figure qty">{{ formatQuantity(ing) || '—' }}</span>
                    <span class="ing-item">{{ ing.item }}</span>
                  </button>
                </div>
              </div>

              <div v-if="mode === 'edit'" class="hd-row">
                <div class="hd-row-margin" aria-hidden="true"></div>
                <div class="hd-row-body">
                  <button class="add-btn" type="button" @click="newIngredientOpen = true">
                    <HdIcon name="plus" :size="15" :weight="2" />
                    <span>Add an ingredient</span>
                  </button>
                </div>
              </div>
            </template>

            <div class="hd-row gap" aria-hidden="true">
              <div class="hd-row-margin"></div>
              <div class="hd-row-body"></div>
            </div>

            <div class="hd-row">
              <div class="hd-row-margin" aria-hidden="true"></div>
              <div class="hd-row-body"><h2 class="hd-label">Method</h2></div>
            </div>

            <!-- A note recorded before step 1 belongs to the recipe. Across,
                 not down: four of them stacked one per margin cell made a
                 ladder of pale marks running eight ruled lines deep with
                 nothing written beside any of them. -->
            <!-- In edit mode, always show a way to add a recipe-level note
                 (voice or written) — the same options each step has inline.
                 When recording is in progress (any target), this row becomes
                 the one visible Stop control with the destination and clock. -->
            <div v-if="mode === 'edit'" class="hd-row">
              <div class="hd-row-margin" aria-hidden="true"></div>
              <div class="hd-row-body recipe-note-add">
                <template v-if="take.recording.value">
                  <button
                    class="add-btn rec-btn rec-live"
                    type="button"
                    :aria-label="`Stop recording — ${takeDestination}`"
                    @click="take.toggle()"
                  >
                    <HdIcon name="stop" :size="13" />
                    <span>Stop</span>
                    <span class="hd-figure rec-clock" role="timer">{{ take.clock.value }}</span>
                  </button>
                  <p class="hd-figure rec-where">{{ takeDestination }}</p>
                </template>
                <template v-else>
                  <button class="add-btn rec-btn" type="button" @click="recordOnRecipe()">
                    <HdIcon name="record" :size="13" />
                    <span>Record a memory</span>
                  </button>
                  <button class="add-btn" type="button" @click="openPin(null)">
                    <HdIcon name="lines" :size="13" />
                    <span>Write a note</span>
                  </button>
                </template>
              </div>
            </div>

            <div v-if="recipeNotes.length" class="hd-row">
              <div class="hd-row-margin" aria-hidden="true"></div>
              <div class="hd-row-body preamble">
                <p class="hd-figure lead">Before the first step</p>
                <!-- READING: marks, across rather than down — four of them
                     stacked one per margin cell made a ladder of pale marks
                     running eight ruled lines deep with nothing written
                     beside any of them.
                     EDITING: the same notes as named rows, because a row of
                     identical discs is unreadable the moment you want to move
                     one or throw it away. -->
                <ul v-if="mode === 'edit'" class="note-rows">
                  <NoteRow
                    v-for="note in recipeNotes"
                    :key="note.id"
                    :note="note"
                    :cook-name="cookName"
                    :playing="isPlaying(note.id)"
                    @play="play"
                    @manage="openManage"
                  />
                </ul>
                <div v-else class="preamble-notes">
                  <MarginNote
                    v-for="note in recipeNotes"
                    :key="note.id"
                    :note="note"
                    :cook-name="cookName"
                    :playing="isPlaying(note.id)"
                    :progress="isPlaying(note.id) ? progress : 0"
                    @play="play"
                    @manage="openManage"
                  />
                </div>
              </div>
            </div>

            <ol v-if="recipeSteps.length" class="step-list">
              <SheetStep
                v-for="(step, i) in recipeSteps"
                :key="step.id"
                :step="step"
                :number="i + 1"
                :notes="notesFor(step.id)"
                :cook-name="cookName"
                :playing-note-id="playingNoteId"
                :progress="progress"
                :editable="mode === 'edit'"
                :ref="(el) => setStepRef(step.id, el)"
                @play="play"
                @manage="openManage"
                @record="recordOnStep"
                @pin="openPin"
              />
            </ol>

            <div v-if="mode === 'edit'" class="hd-row">
              <div class="hd-row-margin" aria-hidden="true"></div>
              <div class="hd-row-body">
                <button class="add-btn" type="button" @click="newStepOpen = true">
                  <HdIcon name="plus" :size="15" :weight="2" />
                  <span>Add a step</span>
                </button>
              </div>
            </div>
          </div>

          <p v-if="!recipeSteps.length && mode !== 'edit'" class="quiet">No method written down yet.</p>

          <!-- The margin teaches nobody on its own: a mark is a mark. One line,
               once, under the method, and only when there is something to hear. -->
          <!-- READING ONLY, and that is a correctness fix rather than a tidy-up:
               editing draws no margin marks at all (the notes are listed under
               their step instead), so this line was teaching a gesture against
               an empty margin — the clearest way to make someone think the app
               is broken is to tell them to tap something that is not there. -->
          <p v-if="!quietMargin && mode === 'read'" class="hd-figure how">
            Tap a mark in the margin to hear what they said. Press and hold one to delete it.
          </p>
        </div>
      </article>

      <!-- ─────────────────────── THE ONE ACTION ─────────────────────── -->
      <!-- Each mode's primary, in the same place, so the bar is a promise
           rather than a surprise: read → cook it, edit → record, cook → next.
           Fixed rather than sitting in the flow, because it is pressed while
           someone is mid-sentence and a control that has scrolled away is a
           control that is not there. -->
      <!-- Said out loud, and only when there is something to act on. Sits above
           the bar rather than inside it: a message that pushes the button
           around is a message that moves the control mid-press. -->
      <div v-if="loaded && recipe && mode === 'edit' && (take.saveError.value || take.message.value)" slot="fixed" class="record-trouble">
        <p class="trouble-text">{{ take.saveError.value || take.message.value }}</p>
        <button v-if="take.pendingClip.value" class="trouble-retry" type="button" @click="take.retrySave()">
          Try saving it again
        </button>
      </div>

      <p class="sr-only" role="status" aria-live="polite">{{ take.announcement.value }}</p>

      <!-- No bar while editing: Done is in the toolbar and Record lives on the
           sheet beside the step it lands on. -->
      <div v-if="loaded && recipe && mode !== 'edit'" slot="fixed" class="action-bar">
        <template v-if="mode === 'read'">
          <button
            v-if="recipeSteps.length"
            class="cta cta-dark"
            type="button"
            @click="toCook"
          >
            <HdIcon name="play" :size="15" />
            <span>Cook this</span>
          </button>
          <button v-else class="cta cta-red" type="button" @click="toEdit">
            <HdIcon name="pencil" :size="15" />
            <span>Write it down</span>
          </button>
        </template>

        <!-- EDITING: the toolbar is enough — Done is the only action from the bar. -->

        <template v-else>
          <button class="cta cta-quiet" type="button" aria-label="Previous step" :disabled="cookIndex === 0" @click="prevStep">
            <HdIcon name="chevron-left" :size="21" :weight="1.8" />
          </button>
          <button class="cta cta-dark grow" type="button" @click="nextStep">
            <span>{{ onLastStep ? 'Done' : 'Next step' }}</span>
            <HdIcon name="chevron-right" :size="18" :weight="1.9" />
          </button>
        </template>
      </div>

      <HdSheet
        :open="recipeMenuOpen"
        :header="recipe?.title || 'This recipe'"
        :buttons="recipeMenuButtons"
        @dismiss="recipeMenuOpen = false"
      />

      <HdDialog
        :open="confirmDelete"
        :header="`Delete ${recipe?.title || 'this recipe'}?`"
        :message="deleteMessage"
        :buttons="deleteButtons"
        @dismiss="confirmDelete = false"
      />

      <HdSheet
        :open="manageNote !== null"
        :header="manageNote?.body || (manageNote?.kind === 'written' ? 'Note' : `Note from ${cookName}`)"
        :buttons="manageButtons"
        @dismiss="manageId = null"
      />

      <HdDialog
        :open="detailsOpen"
        header="This recipe"
        :inputs="detailsInputs"
        :buttons="detailsButtons"
        @dismiss="detailsOpen = false"
      />

      <HdDialog
        :open="editingIngredient !== null"
        header="This ingredient"
        :inputs="ingredientInputs"
        :buttons="ingredientButtons"
        @dismiss="ingredientId = null"
      />

      <HdDialog
        :open="newIngredientOpen"
        header="Add an ingredient"
        :inputs="newIngredientInputs"
        :buttons="newIngredientButtons"
        @dismiss="newIngredientOpen = false"
      />

      <HdDialog
        :open="newStepOpen"
        header="Add a step"
        :inputs="newStepInputs"
        :buttons="newStepButtons"
        @dismiss="newStepOpen = false"
      />

      <HdDialog
        :open="pinOpen"
        header="Write a note"
        sub-header="It sits beside this step"
        :inputs="pinInputs"
        :buttons="pinButtons"
        @dismiss="pinOpen = false"
      />

      <HdDialog
        :open="reader !== null"
        :header="reader?.header"
        :message="reader?.message"
        :buttons="['Close']"
        @dismiss="reader = null"
      />

      <IonToast
        :is-open="toast !== ''"
        :message="toast"
        :duration="3600"
        position="bottom"
        @dismiss="toast = ''"
      />
    </IonContent>
  </IonPage>
</template>

<style scoped>
.waiting { min-height: 100%; }

/* ------------------------------------------------------------- the bar */

/* Toolbar controls, drawn as text with an icon rather than as Ionic buttons:
   this app's chrome is a page, and a pill-shaped button on paper reads as a
   different application. The 44px target is the padding, not the ink. */
.bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 44px;
  padding: 0 10px;
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 500;
  color: var(--hd-pencil);
  cursor: pointer;
}
/* Measured 34x44 in the built app — 10px short on width, on the control that
   opens the only irreversible action in the product. */
.bar-btn.icon-only { padding: 0 13px; min-width: 44px; }
.bar-btn.strong { font-weight: 600; color: var(--hd-sheet); }
.bar-title {
  font-family: var(--hd-sans);
  font-size: var(--hd-t-figure);
  color: var(--hd-pencil);
  padding-right: 12px;
}

/* THE EDITING BAR IS DARK, and that is load-bearing rather than decorative.
   The cost of a mode is a mode you are in without realising; the fix is that
   it must LOOK unmistakably different, not merely behave differently. */
.hd-bar.editing ion-toolbar {
  --background: var(--hd-ink);
  --color: var(--hd-sheet);
  --border-color: var(--hd-ink);
}
.mode-flag {
  font-family: var(--hd-sans);
  font-size: var(--hd-t-micro);
  font-weight: 700;
  letter-spacing: 0.16em;
  color: var(--hd-sheet);
  padding-left: 14px;
}

/* ------------------------------------------------------------ the sheet */

/* The bottom padding clears the fixed action bar, which overlays the scroll
   area rather than shortening it. The ruled-line arithmetic lives inside
   .hd-field and is untouched by it. */
.sheet {
  padding: 24px 0 calc(96px + env(safe-area-inset-bottom));
  min-height: 100%;
}

.head { padding: 0 20px 22px 20px; }
.title { margin: 0; font-size: var(--hd-t-title); font-weight: 500; line-height: 1.1; letter-spacing: -0.015em; }
.attribution { margin: 7px 0 0; font-style: italic; color: var(--hd-pencil); }

/* These were a heading and a line of prose before they became controls, and
   they have to go on reading as a heading and a line of prose. A recipe with a
   button ring round its title is a form, not a page. Disabled in reading, so
   the sheet you cook from cannot be edited by a stray thumb — and a disabled
   button must not go grey, because this one is the recipe's name. */
.title-btn,
.occasion-btn {
  display: block;
  width: 100%;
  padding: 0;
  /* A tap target only while it IS one. Disabled in reading mode it is the
     recipe's name and must not reserve 44px of nothing. */
  min-height: 0;
  margin: 0;
  background: none;
  border: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
/* `font: inherit` above is the shorthand, so it resets font-size and weight
   too — and because these rules come after .title and .attribution, it won
   and the recipe's name rendered at body size. The name is the one piece of
   type on this screen that has to carry, so it is restated here at the
   doubled specificity rather than relying on source order, which is exactly
   the tie that bit .ing-row once already. */
.title.title-btn { font-size: var(--hd-t-title); font-weight: 500; line-height: 1.1; letter-spacing: -0.015em; }
.attribution.occasion-btn { font-size: var(--hd-t-body); font-style: italic; }

.title-btn:enabled,
.ing-btn:enabled { min-height: 44px; }
.title-btn:disabled,
.occasion-btn:disabled,
.ing-btn:disabled { cursor: default; opacity: 1; color: inherit; }
.occasion-btn { min-height: 44px; }
.occasion-btn:disabled { min-height: 0; }
/* Never set, so it has to invite rather than look like a fact. */
.occasion-btn:not(:disabled):not(:hover):not(:focus-visible) { color: var(--hd-pencil); }

/* An ingredient line: the amounts right-align into their own column, because
   mid-cook you scan the numbers, not the sentences. One ruled line each. */
.ing-btn {
  display: grid;
  /* minmax, not a flat 72px. At a fixed width "3 tablespoons" broke INSIDE
     the word — three ruled lines reading "3 / tablespoon / s", with the item
     name aligned to the first, so the column built for scanning numbers at
     arm's length returned nonsense. It may now grow to fit its longest
     amount, and a genuinely long one ("until it looks right", which this app
     promises to keep verbatim) wraps as whole words. */
  grid-template-columns: minmax(72px, max-content) 1fr;
  gap: 13px;
  align-items: baseline;
  width: 100%;
  padding: 0;
  background: none;
  border: 0;
  text-align: left;
  font: inherit;
  color: var(--hd-ink);
  cursor: pointer;
}
.qty {
  text-align: right;
  color: var(--hd-ink);
  line-height: var(--hd-rule);
  overflow-wrap: normal;
  word-break: keep-all;
}
.ing-item { font-size: var(--hd-t-body); line-height: var(--hd-rule); }

/* One blank ruled line between the two halves of the page. A margin would move
   the writing off the ruling; an empty line is what you would leave by hand. */
.gap { height: var(--hd-rule); }

/* 56px, not 44: a real tap target AND a whole number of ruled lines — 44 is
   neither, and one add button 16px off the pitch drags every line under it off
   the ruling for the rest of the page.
 *
 * BUT THE BOX BEING ON THE GRID IS NOT THE WRITING BEING ON THE LINE. Centred
 * in 56px, the words floated exactly between two rules, which is the one place
 * writing never sits on ruled paper. The content is aligned to the TOP of the
 * box and each child is one rule tall, so the label sits on the first line and
 * the second line is the tap target's breathing room. */
.add-btn {
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  min-height: calc(var(--hd-rule) * 2);
  padding: 0;
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 600;
  color: var(--hd-margin);
  cursor: pointer;
}
/* Each child one rule tall, so the icon centres on the same line the label
   sits on rather than on the boundary between two. */
.add-btn > * { height: var(--hd-rule); display: flex; align-items: center; }
.add-btn span { line-height: var(--hd-rule); }

/* The recipe-level note-add row: Record a memory + Write a note, side by side
   with a gap. When recording is live, the rec-live button spans the whole row. */
.recipe-note-add {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0 20px;
}
.rec-btn { color: var(--hd-margin); }
/* Live recording: the button turns ink-coloured so it reads as "press to stop",
   separate from the red that starts it. */
.rec-live { color: var(--hd-ink); }
.rec-clock { font-size: var(--hd-t-figure); color: var(--hd-pencil); margin-left: 4px; }
.rec-where {
  flex-basis: 100%;
  margin: 0;
  font-style: italic;
  line-height: var(--hd-rule);
}

.step-list { margin: 0; padding: 0; list-style: none; }

/* The recipe-level notes, across rather than down. `gap` is horizontal only —
   the row wraps on a narrow phone rather than scrolling sideways, because a
   recipe page does not scroll sideways. */
.preamble { padding-bottom: var(--hd-rule); }
.preamble-notes { display: flex; flex-wrap: wrap; gap: 0 14px; }
.note-rows { margin: 0; padding: 0; list-style: none; }
.lead { margin: 0; line-height: var(--hd-rule); }

/* Said once, at the bottom, in the pencil the rest of the asides use. It is
   instruction rather than recipe, so it must not look like a step. */
.how {
  margin: 20px 16px 0 calc(var(--hd-margin-w) + 32px);
  max-width: 34em;
  line-height: 1.5;
  font-style: italic;
}
.quiet {
  margin: 20px 16px 0 calc(var(--hd-margin-w) + 32px);
  max-width: 30em;
  color: var(--hd-pencil);
  font-style: italic;
}

/* ----------------------------------------------------------- the cooking */

.cooking {
  min-height: 100%;
  padding: 4px 24px calc(96px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
}

.progress { display: flex; gap: 5px; padding: 4px 0 26px; }
.pip { height: 3px; flex-grow: 1; border-radius: 2px; background: var(--hd-feint); }
.pip.done { background: var(--hd-ink); }
.pip.now { background: var(--hd-margin); }

.cook-count {
  margin: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-micro);
  font-weight: 700;
  letter-spacing: 0.16em;
  color: var(--hd-pencil);
}

/* The same ruled paper, at double pitch — 56px is two rules, so cooking mode
   and the sheet share one grid rather than being two unrelated surfaces.
   No vertical padding, for the same reason .hd-field has none: padding moves
   the writing without moving the rules. */
.cook-field {
  position: relative;
  margin-top: 16px;
  padding-left: 34px;
  background-image: linear-gradient(var(--hd-rule-ink) 1px, transparent 1px);
  background-size: 100% calc(var(--hd-rule) * 2);
  background-position: 0 calc(var(--hd-rule) * 2 - 1px);
}
.cook-rule {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 18px;
  width: 1px;
  background: var(--hd-margin-ink);
  opacity: 0.85;
}

/* The step at the size you can read from the other side of a bench, with the
   phone flat on it and your hands in the mince. */
.cook-body {
  margin: 0;
  /* Was a literal 31px beside a --hd-t-title of 30, for no reason a reader
     could name. The one screen that is read at arm's length was the one
     bypassing the type scale. */
  font-size: calc(var(--hd-t-title) + 2px);
  /* Two rules, so the big writing sits on the wide ruling the way the small
     writing sits on the narrow one. */
  line-height: calc(var(--hd-rule) * 2);
  letter-spacing: -0.012em;
}

/* --------------------------------------------------------- the amounts */

.cook-ing { border-top: 1px solid var(--hd-feint); }
.cook-ing-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 0;
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 600;
  color: var(--hd-pencil);
  cursor: pointer;
}
.cook-ing-list { margin: 0 0 10px; padding: 0; list-style: none; }
.cook-ing-row {
  display: grid;
  grid-template-columns: minmax(72px, max-content) 1fr;
  gap: 13px;
  line-height: var(--hd-rule);
}
.cook-qty { text-align: right; color: var(--hd-ink); overflow-wrap: normal; word-break: keep-all; }
.cook-item { font-size: var(--hd-t-body); }

.said {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-top: 22px;
  padding: 16px;
}
.said-play {
  width: 54px;
  height: 54px;
  border-radius: 27px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--hd-margin);
  color: var(--hd-sheet);
  border: 0;
  cursor: pointer;
  box-shadow: 0 2px 7px color-mix(in srgb, var(--hd-margin) 38%, transparent);
}
.said-play[data-playing='true'] { background: color-mix(in srgb, var(--hd-margin) 78%, black); }
/* A written note is not audio and does not wear the red disc either. */
.said-play.written {
  background: var(--hd-sheet);
  color: var(--hd-pencil);
  border: 1.5px solid var(--hd-feint);
  box-shadow: none;
}
.said-text { min-width: 0; }
.said-who {
  margin: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-figure);
  font-weight: 600;
  letter-spacing: 0.02em;
}
.said-who .hd-figure { margin-left: 4px; }
.said-body { margin: 4px 0 0; font-style: italic; color: var(--hd-pencil); line-height: 22px; }

.cook-spacer { flex-grow: 1; min-height: 18px; }

.cook-record {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 0;
  background: none;
  border: 0;
  border-top: 1px solid var(--hd-feint);
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 600;
  color: var(--hd-margin);
  cursor: pointer;
}

/* --------------------------------------------------------- the action bar */

/* Ionic gives [slot=fixed] `position: absolute` against the scroll host, so
   this is pinned to the content box and does not move with the paper.
   `position` is restated rather than inherited on purpose: a bar that silently
   starts scrolling is a whole class of bug on its own. */
.action-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  gap: 11px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: var(--hd-paper);
}

.cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  flex-grow: 1;
  min-height: 56px;
  padding: 0 20px;
  font-family: var(--hd-serif);
  font-size: var(--hd-t-name);
  font-weight: 500;
  border: 0;
  cursor: pointer;
}
.cta.grow { flex-grow: 1; }

/* COOK IS THE PRIMARY, and it is ink rather than red. Red in this app means
   recording; the thing you opened the recipe to do is not a recording. */
.cta-dark {
  background: var(--hd-ink);
  color: var(--hd-sheet);
  box-shadow: 0 2px 6px color-mix(in srgb, var(--hd-ink) 30%, transparent);
}
/* Red, outlined: this is recording, and recording is one of the two things the
   red is allowed to mean. Solid red belongs to a recorder actually running. */
.cta-red {
  background: var(--hd-sheet);
  color: var(--hd-margin);
  border: 2px solid var(--hd-margin);
}
/* Recording in progress: filled, because the recorder IS running now — the
   outline version is the invitation, this is the state. */
.cta-recording {
  background: var(--hd-margin);
  color: var(--hd-sheet);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--hd-margin) 40%, transparent);
}
.cta-recording .clock { color: var(--hd-sheet); font-size: var(--hd-t-figure); }


.record-trouble {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: calc(16px + env(safe-area-inset-bottom));
  background: var(--hd-sheet);
  border: 1px solid var(--hd-margin);
  padding: 12px 14px;
}
.trouble-text { margin: 0; font-size: var(--hd-t-small); line-height: 1.45; color: var(--hd-ink); }
.trouble-retry {
  min-height: 44px;
  margin-top: 4px;
  padding: 0;
  background: none;
  border: 0;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-control);
  font-weight: 600;
  color: var(--hd-margin);
  cursor: pointer;
}

/* Announced, never drawn: the clock beside the button is the visible form. */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.cta-quiet {
  flex-grow: 0;
  width: 82px;
  background: var(--hd-sheet);
  color: var(--hd-pencil);
  border: 1px solid var(--hd-feint);
}
.cta-quiet:disabled { opacity: 0.4; cursor: default; }

/* The bar sits outside .hd-page, so the app's focus ring does not reach it.
   Teal, never red — red is the recording, not the keyboard. */
.cta:focus-visible,
.bar-btn:focus-visible,
.cook-record:focus-visible {
  outline: 2px solid var(--hd-focus);
  outline-offset: 2px;
}

.missing { padding: 48px 20px; min-height: 100%; text-align: center; color: var(--hd-pencil); }
.missing-h { margin: 0 0 8px; font-size: var(--hd-t-heading); font-weight: 500; color: var(--hd-ink); }
.missing-p { margin: 0 auto 20px; max-width: 26em; font-style: italic; }
.back { min-height: 44px; padding: 0 16px; background: none; border: 0; color: var(--hd-margin); cursor: pointer; }

/* Printing is a real output for this app — a recipe on the fridge is the
   format it came from. What must not print: the chrome, the controls, and the
   marks for recordings that a sheet of paper cannot play. */
@media print {
  .action-bar,
  .hd-bar,
  .how { display: none !important; }
  .sheet { padding: 0; }
}
</style>
